import React, { useMemo, useState } from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination
} from "react-table";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Typography,
  alpha
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { fNumber } from "../utils/formatNumber";

const GlobalSearch = ({ globalFilter, setGlobalFilter, totalCount, filteredCount }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1.5,
      p: { xs: 1.5, sm: 2 },
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FAFCFE")
    }}
  >
    <TextField
      size="small"
      placeholder="Search records by any field..."
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      sx={{
        width: { xs: "100%", sm: 340 },
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          backgroundColor: "background.paper",
          fontSize: "0.875rem",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "primary.main"
          },
          "&.Mui-focused": {
            boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
          }
        }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          </InputAdornment>
        ),
        endAdornment: globalFilter ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setGlobalFilter(undefined)}
              edge="end"
              aria-label="clear search"
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }}
    />

    <Box display="flex" alignItems="center" gap={1}>
      <Chip
        size="small"
        variant="outlined"
        label={
          globalFilter
            ? `${filteredCount} of ${totalCount} records found`
            : `${totalCount} records`
        }
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          borderColor: "divider",
          backgroundColor: "background.paper",
          color: "text.secondary"
        }}
      />
    </Box>
  </Box>
);

const renderStatusChip = (val) => {
  const lower = String(val || "").toLowerCase().trim();
  if (lower === "credit" || lower === "deposit") {
    return (
      <Chip
        label="Credit"
        size="small"
        sx={{
          height: 22,
          fontSize: "0.72rem",
          fontWeight: 700,
          backgroundColor: "#ECFDF5",
          color: "#047857",
          border: "1px solid #A7F3D0"
        }}
      />
    );
  }
  if (lower === "debit" || lower === "withdrawal") {
    return (
      <Chip
        label="Debit"
        size="small"
        sx={{
          height: 22,
          fontSize: "0.72rem",
          fontWeight: 700,
          backgroundColor: "#FFF1F2",
          color: "#B91C1C",
          border: "1px solid #FECDD3"
        }}
      />
    );
  }
  if (lower === "approved loan" || lower === "buy") {
    return (
      <Chip
        label={val}
        size="small"
        sx={{
          height: 22,
          fontSize: "0.72rem",
          fontWeight: 700,
          backgroundColor: "#F0F9FF",
          color: "#0369A1",
          border: "1px solid #BAE6FD"
        }}
      />
    );
  }
  if (lower === "loan repayment" || lower === "repayment") {
    return (
      <Chip
        label={val}
        size="small"
        sx={{
          height: 22,
          fontSize: "0.72rem",
          fontWeight: 700,
          backgroundColor: "#FFFBEB",
          color: "#B45309",
          border: "1px solid #FDE68A"
        }}
      />
    );
  }
  return val;
};

export default function BeautifulTable({
  columns,
  data,
  pageSize = 10,
  renderRowActions,
  onRowClick,
  sx
}) {
  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data || [], [data]);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    state: { pageIndex, pageSize: statePageSize, globalFilter },
    setGlobalFilter,
    setPageSize,
    gotoPage,
  } = useTable(
    {
      columns: memoColumns,
      data: memoData,
      initialState: { pageIndex: 0, pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        backgroundColor: "background.paper",
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.04)",
        ...sx
      }}
    >
      <GlobalSearch
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        totalCount={memoData.length}
        filteredCount={rows.length}
      />

      <TableContainer
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": {
            height: 6,
            width: 6
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.12)",
            borderRadius: 3
          }
        }}
      >
        <Table size="small" {...getTableProps()} sx={{ minWidth: { xs: 680, md: "100%" } }}>
          <TableHead>
            {headerGroups.map((hg) => (
              <TableRow
                key={hg.id}
                {...hg.getHeaderGroupProps()}
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
                  borderBottom: `2px solid ${theme.palette.divider}`
                })}
              >
                {hg.headers.map((col) => {
                  const sortProps = col.getSortByToggleProps ? col.getSortByToggleProps() : {};
                  const isAmountCol = /(amount|balance|newbalance|amt|rate|fee)/i.test(
                    String(col.id || col.Header || "")
                  );

                  return (
                    <TableCell
                      key={col.id}
                      {...col.getHeaderProps(sortProps)}
                      align={isAmountCol ? "right" : "left"}
                      sx={{
                        py: 1.5,
                        px: 2,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "text.secondary",
                        cursor: col.canSort ? "pointer" : "default",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                        "&:hover": col.canSort
                          ? {
                              color: "primary.main"
                            }
                          : {}
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={isAmountCol ? "flex-end" : "flex-start"}
                        gap={0.5}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            color: "inherit"
                          }}
                        >
                          {col.render("Header")}
                        </Typography>
                        {col.isSorted ? (
                          col.isSortedDesc ? (
                            <ArrowDownwardRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />
                          ) : (
                            <ArrowUpwardRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />
                          )
                        ) : col.canSort ? (
                          <UnfoldMoreRoundedIcon
                            sx={{ fontSize: 16, color: "text.disabled", opacity: 0.4 }}
                          />
                        ) : null}
                      </Box>
                    </TableCell>
                  );
                })}
                {renderRowActions ? (
                  <TableCell
                    align="right"
                    sx={{
                      py: 1.5,
                      px: 2,
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "text.secondary",
                      width: 100
                    }}
                  >
                    Actions
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headerGroups[0].headers.length + (renderRowActions ? 1 : 0)}
                  sx={{ py: 6, borderBottom: "none" }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                  >
                    <SearchOffRoundedIcon sx={{ fontSize: 42, color: "text.disabled", opacity: 0.6 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      No matching records found
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>
                      {globalFilter ? "Try adjusting your search terms" : "There are currently no records to display"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              page.map((row) => {
                prepareRow(row);
                const txType = String(
                  row.original?.creditOrDebit || row.original?.type || ""
                ).toLowerCase().trim();
                const isSelected = selectedRowId === row.id;

                return (
                  <TableRow
                    key={row.id}
                    {...row.getRowProps()}
                    onClick={() => {
                      setSelectedRowId(row.id);
                      if (onRowClick) onRowClick(row.original);
                    }}
                    sx={(theme) => ({
                      cursor: onRowClick ? "pointer" : "default",
                      backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.08)
                        : "transparent",
                      transition: "background-color 140ms ease-in-out",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.035)
                      },
                      borderLeft: `3px solid ${
                        txType === "credit"
                          ? "#10B981"
                          : txType === "debit"
                          ? "#EF4444"
                          : "transparent"
                      }`,
                      "& td": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 1.25,
                        px: 2,
                        fontSize: "0.84rem",
                        color: "text.primary"
                      }
                    })}
                  >
                    {row.cells.map((cell) => {
                      const raw = cell.value;
                      const colId = (
                        cell.column.id ||
                        (typeof cell.column.Header === "string" ? cell.column.Header : "")
                      )
                        .toString()
                        .toLowerCase();
                      const isAmountCol = /(amount|balance|newbalance|new_balance|amt|total)/i.test(colId);
                      const isTypeCol = /(creditordebit|type|trxtype)/i.test(colId);

                      const cleanNum =
                        raw !== null && raw !== undefined && raw !== ""
                          ? Number(String(raw).replace(/,/g, ""))
                          : NaN;
                      const isNumeric = Number.isFinite(cleanNum);

                      let display;
                      if (isTypeCol && typeof raw === "string") {
                        display = renderStatusChip(raw);
                      } else if (isNumeric && isAmountCol) {
                        display = (
                          <span
                            style={{
                              fontVariantNumeric: "tabular-nums",
                              fontWeight: 600,
                              letterSpacing: "0.01em"
                            }}
                          >
                            ₦{fNumber(cleanNum)}
                          </span>
                        );
                      } else {
                        display = cell.render("Cell");
                      }

                      return (
                        <TableCell
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          align={isAmountCol ? "right" : "left"}
                          sx={{
                            whiteSpace: isAmountCol ? "nowrap" : "normal"
                          }}
                        >
                          {display}
                        </TableCell>
                      );
                    })}
                    {renderRowActions ? (
                      <TableCell align="right">
                        {renderRowActions(row.original)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          p: { xs: 1, sm: 1.5 },
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.01)" : "#FAFCFE"
        }}
      >
        <TablePagination
          component="div"
          count={memoData.length}
          page={pageIndex}
          onPageChange={(_, newPage) => gotoPage(newPage)}
          rowsPerPage={statePageSize}
          onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            border: "none",
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
              fontSize: "0.8rem",
              color: "text.secondary"
            },
            ".MuiTablePagination-toolbar": {
              minHeight: 44,
              px: 1
            }
          }}
        />
      </Box>
    </Paper>
  );
}